import useSWR from "swr";
import fetcher from "@adora/shared/utils/fetch";
import { RelativeUrl } from "@adora/shared/utils/environment";
import patch from "@adora/shared/utils/patch";

export interface ExperimentCreative {
  id: number;
  experiment_id: number;
  is_blob: boolean | null;
  is_wrong_shoe: boolean | null;
  is_incoherent: boolean | null;
  is_accepted_auto: boolean | null;
  is_blob_manual: boolean | null;
  is_wrong_shoe_manual: boolean | null;
  is_incoherent_manual: boolean | null;
  is_accepted_manual: boolean | null;
  is_bad_laces_manual: boolean | null;
  is_bad_fine_details_manual: boolean | null;
  img_url: string;
}

export interface ExperimentCreativeUpdateRequest {
  is_blob_manual?: boolean | null;
  is_wrong_shoe_manual?: boolean | null;
  is_incoherent_manual?: boolean | null;
  is_bad_laces_manual?: boolean | null;
  is_bad_fine_details_manual?: boolean | null;
  is_accepted_manual?: boolean | null;
}

function getExperimentCreativesUrl(brandId: string | number | undefined, experimentId: string | number | undefined) {
  if (!brandId || !experimentId) return null;

  const baseUrl = `${RelativeUrl}/experiment_creatives`;
  return `${baseUrl}?brand_id=${encodeURIComponent(String(brandId))}&experiment_id=${encodeURIComponent(String(experimentId))}`;
}

export default function useExperimentCreatives(
  brandId: string | number | undefined,
  experimentId: string | number | undefined
) {
  const { data, error, isLoading, mutate } = useSWR(
    getExperimentCreativesUrl(brandId, experimentId),
    (url) => fetcher<ExperimentCreative[]>(url),
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true
    }
  );

  async function update(creativeId: number, updateData: ExperimentCreativeUpdateRequest) {
    const requestBody: Record<string, boolean> = {};

    const fields: Array<keyof ExperimentCreativeUpdateRequest> = [
      "is_blob_manual",
      "is_wrong_shoe_manual",
      "is_incoherent_manual",
      "is_bad_laces_manual",
      "is_bad_fine_details_manual",
      "is_accepted_manual"
    ];

    for (const field of fields) {
      if (field in updateData && updateData[field] !== undefined) {
        requestBody[field] = updateData[field] as boolean;
      }
    }

    const updateResult = await patch<ExperimentCreative>(
      `${RelativeUrl}/experiment_creatives/${creativeId}`,
      { brand_id: brandId },
      requestBody
    );

    updateResult.match(
      (e) => console.error(e.message),
      (updatedCreative) => {
        mutate(
          (prevCreatives) =>
            prevCreatives?.map((creative) => (creative.id === creativeId ? updatedCreative : creative)) ?? [],
          false
        );
      }
    );

    return updateResult;
  }

  return {
    creatives: data || [],
    isLoading,
    error,
    mutate,
    update
  };
}
